import { useState } from 'react'
import RightOutlined from '@ant-design/icons/RightOutlined'
import Modal from 'antd/lib/modal'
import Button from 'antd/lib/button'
import Checkbox from 'antd/lib/checkbox'
import Alert from 'antd/lib/alert'
import { PermissionItem } from './PermissionItem'
import { PermissionsDisplay } from './PermissionsDisplay'
import { ComplianceBannerMessage } from './ComplianceBannerMessage'
import { useProctoringPermissionItems } from './useProctoringPermissionItems'
import { isSecureProctoringProfile } from 'Config/proctoring.config'
import styles from './ProctoringPermissionsGate.module.less'
import {
    messageForDisconnectReason,
    messageForPermissionError,
} from 'Components/Proctoring/proctoringPermissions.helpers'
import type { ProctoringPermissionsGateProps } from 'Components/Proctoring/proctoring.types'

export const ProctoringPermissionsGate = ({
    proctoring,
    browserSupported,
    onPermissionsGranted,
    profile,
    disconnectReason = null,
}: ProctoringPermissionsGateProps) => {
    const isSecure = isSecureProctoringProfile(profile)
    const [hasConsent, setHasConsent] = useState(false)
    const [permissionError, setPermissionError] = useState<string | null>(() =>
        messageForDisconnectReason(disconnectReason)
    )
    const { permissionItems, refreshDevices, webcamStream, screenStream } =
        useProctoringPermissionItems(proctoring, profile)

    const isLoading = proctoring.status === 'requesting-permissions'
    const { isReady } = proctoring
    const disconnectError = !isReady ? messageForDisconnectReason(disconnectReason) : null

    const handleGrant = async () => {
        setPermissionError(null)
        try {
            await proctoring.requestPermissions()
            await refreshDevices()
        } catch (error) {
            setPermissionError(messageForPermissionError(error))
        }
    }

    const modalHeader = (
        <div className={styles.modalHeader}>
            <h2 className={styles.modalTitle}>System Permissions</h2>
            <p className={styles.modalDescription}>
                Ensure all required system permissions are granted for a smooth exam experience.
            </p>
        </div>
    )

    let body = null

    if (!browserSupported) {
        body = (
            <div data-testid="proctoring-unsupported">
                <Alert
                    type="error"
                    showIcon
                    message="Proctoring is not supported in this browser"
                    description="Please use a supported desktop browser to continue."
                />
            </div>
        )
    } else {
        body = (
            <div className={styles.panel} data-testid="proctoring-permissions-gate">
                <div className={styles.grid}>
                    <div className={styles.permissionList}>
                        {permissionItems.map(item => (
                            <PermissionItem
                                key={item.kind}
                                label={item.label}
                                kind={item.kind}
                                status={item.status}
                                availableDevices={item.availableDevices}
                                hideAvailableDevices={item.hideAvailableDevices}
                                selectedDeviceId={item.selectedDeviceId}
                                onDeviceChange={item.onDeviceChange}
                            />
                        ))}
                    </div>
                    <PermissionsDisplay
                        videoStream={webcamStream}
                        screenStream={screenStream}
                        showVideoTile={!isSecure}
                        showScreenTile
                    />
                </div>

                <div className={styles.footerBlock}>
                    <div className={styles.alertStack}>
                        {permissionError || disconnectError ? (
                            <Alert
                                className={styles.alertBox}
                                type="error"
                                showIcon
                                message={permissionError || disconnectError}
                            />
                        ) : null}
                        <Alert
                            className={styles.alertBox}
                            type="info"
                            showIcon
                            message={<ComplianceBannerMessage />}
                        />
                    </div>
                    <div className={styles.consentRow}>
                        <Checkbox
                            checked={hasConsent}
                            onChange={e => setHasConsent(e.target.checked)}
                            className={styles.consentCheckbox}
                            data-testid="proctoring-permission-consent"
                        >
                            <span className={styles.consentLabel}>
                                {isSecure
                                    ? 'I give consent to access my system features (screen) for this exam.'
                                    : 'I give consent to access my system features (camera, screen, audio) for this exam.'}
                            </span>
                        </Checkbox>
                    </div>
                </div>

                <div className={styles.grantRow}>
                    <Button
                        type="default"
                        className={styles.grantButton}
                        disabled={!hasConsent || isLoading || isReady}
                        loading={isLoading}
                        onClick={handleGrant}
                        data-testid="proctoring-grant-permissions"
                    >
                        {isReady ? 'Permission Granted' : 'Grant Permission'}
                    </Button>
                </div>

                {isReady ? (
                    <div className={styles.footerActions}>
                        <Button
                            type="primary"
                            className={styles.continueButton}
                            onClick={onPermissionsGranted}
                            data-testid="proctoring-permissions-continue"
                        >
                            Continue
                            <RightOutlined className={styles.continueChevron} />
                        </Button>
                    </div>
                ) : null}
            </div>
        )
    }

    return (
        <Modal
            open
            centered
            closable={false}
            maskClosable={false}
            keyboard={false}
            footer={null}
            width={560}
            title={modalHeader}
            rootClassName={styles.permissionsModal}
            data-testid="proctoring-permissions-modal"
        >
            {body}
        </Modal>
    )
}
